<template>
  <div>
    <Warning v-if="computeTotalPropValue(0) > 100000"/>
    <FormHeading>Who owns the property?</FormHeading>
    <FormInputLabel class="radio-check-heading">Named Insured</FormInputLabel>
    <CheckboxButtons
      :value="property_owner_is_entity"
      name="property_owner_is_entity"
      label
      @change="handlePropertyOwnerEntityChange"
      :items="entityOptions"
    />
    <FormGroup v-if="property_owner_is_entity.length">
      <TextInput
        v-model="entityName"
        name="entityName"
        label="Company Name"
        type="text"
        :error="errors['entityName']"
        @input="removeErrorMessage('entityName', $event)"
      ></TextInput>
      <Suggest 
        v-model="naic" 
        name="naic" 
        label="Industry" 
        :items="naicCodes" 
        titleField="title" 
        :hasFocus="true"
        @change="naic = $event"
        hint="Optional"
      />
    </FormGroup>
    <FormGroup v-if="!property_owner_is_entity.length">
      <TextInput
        v-model="firstName"
        name="firstName"
        label="First Name"
        type="text"
        :error="errors['firstName']"
        @input="removeErrorMessage('firstName', $event)"
      ></TextInput>
      <TextInput
        v-model="lastName"
        name="lastName"
        label="Last Name"
        type="text"
        :error="errors['lastName']"
        @input="removeErrorMessage('lastName', $event)"
      ></TextInput>
    </FormGroup>
    <FormInputLabel>Contact Information</FormInputLabel>
      <FormGroup v-if="property_owner_is_entity.length">
        <TextInput
          v-model="contactFirstName"
          name="contactFirstName"
          label="First Name"
          type="text"
          :error="errors['contactFirstName']"
          @input="removeErrorMessage('contactFirstName', $event)"
        ></TextInput>
        <TextInput
          v-model="contactLastName"
          name="contactLastName"
          label="Last Name"
          type="text"
          :error="errors['contactLastName']"
          @input="removeErrorMessage('contactLastName', $event)"
        ></TextInput>
      </FormGroup>
      <FormGroup>
        <TextInput
          v-model="email"
          name="email"
          label="Email"
          type="text"
          :error="errors['email']"
          @input="removeErrorMessage('email', $event)"
        ></TextInput>
        <Phone
          v-model="phone"
          name="phone"
          label="Phone"
          type="text"
          :error="errors['phone']"
          @input="removeErrorMessage('phone', $event)"
        ></Phone>
    </FormGroup>
    <FormInputLabel>Mailing Address</FormInputLabel>
    <div class="form-group fg-map" @keydown.enter.prevent>
      <SearchAddress :formName="'owner'" @get-address="getAddress"/>
    </div>
    <FormGroup>
      <TextInput
        v-model="streetNumber"
        name="streetNumber"
        label="Street Number & Name"
        type="text"
        :error="errors['streetNumber']"
        @input="removeErrorMessage('streetNumber', $event)"
      />
      <TextInput
        v-model="unitNumber"
        name="unitNumber"
        label="Apt./Unit Number"
        type="text"
      />
    </FormGroup>
    <FormGroup>
      <TextInput 
        v-model="city"
        name="city"
        label="City"
        type="text"
        :error="errors['city']"
        @input="removeErrorMessage('city', $event)"
      />
      <Suggest 
        v-model="state" 
        name="state" 
        label="State" 
        :items="states" 
        titleField="display" 
        :hasFocus="true"
        @change="handleStateChange"
        :error="errors['state']"
      />
      <TextInput
        v-model="zipCode"
        name="zipCode"
        label="Zip Code"
        type="number"
        :error="errors['zipCode']"
        @input="removeErrorMessage('zipCode', $event)"
      />
    </FormGroup>
    <div class="button-group">
      <Button class="transparent" @click="prev">Back</Button>
      <Button class="red" @click="checkForm">Continue</Button>
    </div>
  </div>
</template>

<script>
import {
  FormHeading,
  FormInputLabel,
  Button,
  TextInput,
  FormGroup,
  CheckboxButtons,
  Suggest,
  Phone
} from "../../style";
import Warning from "../Warning";
import { STATES } from "../../utils/constants";
import SearchAddress from "./SearchAddress";
import { mapState } from "vuex";
import { scrollTop } from "../../utils";
export default {
  name: "PropertyOwner",
  props: {
    step: {
      type: Number,
      required: true
    },
    properties: {
      type: Array,
      default: () => []
    },
    property: {
      type: Number,
      default: 1
    },
    propertyOwner: {
      type: Object,
      default:() => {}
    },
    payment: {
      type: Object,
      default: () => {}
    },
    triaItem: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    FormHeading,
    FormInputLabel,
    Button,
    TextInput,
    FormGroup,
    CheckboxButtons,
    Suggest,
    SearchAddress,
    Phone,
    Warning
  },
  mounted() {
    scrollTop();
  },
  data() {
    return {
      entityName: "",
      firstName: "",
      lastName: "",
      contactFirstName: "",
      contactLastName: "",
      email: "",
      phone: "",
      streetNumber: "",
      ownerStreetNumber: "",
      address1: "",
      address2: "",
      unitNumber: "",
      zipCode: "",
      city: "",
      state: null,
      county: "",
      lat: "",
      lng: "",
      naic: null,
      property_owner_is_entity: null,
      emailValidator: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      errors: {
        entityName: "",
        firstName: "",
        lastName: "",
        contactFirstName: "",
        contactLastName: "",
        email: "",
        streetNumber: "",
        city: "",
        zipCode: "",
        state: "",
        phone: ""
      },
      errorFields: []
    };
  },
  methods: {
    checkForm() {
      let preventSubmit = false;
      this.errorFields = [];
      if (this.property_owner_is_entity.length) {
        if (this.entityName.trim().length === 0) {
          this.errors["entityName"] = "Name insured is required.";
          this.errorFields.push("entityName");
          preventSubmit = true;
        }
        if (this.contactFirstName.trim().length === 0) {
          this.errors["contactFirstName"] = "First name is required.";
          this.errorFields.push("contactFirstName");
          preventSubmit = true;
        }
        if (this.contactLastName.trim().length === 0) {
          this.errors["contactLastName"] = "Last name is required.";
          this.errorFields.push("contactLastName");
          preventSubmit = true;
        }
      } else {
        if (this.firstName.trim().length === 0) {
          this.errors["firstName"] = "First name is required.";
          this.errorFields.push("firstName");
          preventSubmit = true;
        }
        if (this.lastName.trim().length === 0) {
          this.errors["lastName"] = "Last name is required.";
          this.errorFields.push("lastName");
          preventSubmit = true;
        }
      }
      if (this.email.trim().length === 0) {
        this.errors["email"] = "Email is required.";
        preventSubmit = true;
      } else if (!this.emailValidator.test(this.email.trim())) {
        this.errors["email"] = "Please use a valid email address.";
        this.errorFields.push("email");
        preventSubmit = true;
      }
      if (this.phone.trim().length !== 12) {
        this.errors["phone"] = "Please enter a valid 10-digit phone number.";
        this.errorFields.push("phone");
        preventSubmit = true;
      }
      if (this.streetNumber.trim().length === 0) {
        this.errors["streetNumber"] = "Street address is required.";
        this.errorFields.push("streetNumber");
        preventSubmit = true;
      }
      if (this.city.trim().length === 0) {
        this.errors["city"] = "City is required.";
        this.errorFields.push("city");
        preventSubmit = true;
      }
      if (this.state === null || this.state === undefined) {
        this.errors["state"] = "State is required.";
        this.errorFields.push("state");
        preventSubmit = true;
      }
      if (this.zipCode.trim().length === 0) {
        this.errors["zipCode"] = "Zip code is required.";
        this.errorFields.push("zipCode");
        preventSubmit = true;
      }
      if(this.errorFields.length > 0)
        document.getElementById(this.errorFields[0]).scrollIntoView({behavior: "smooth", block: "center"});
      if (!preventSubmit) {
        this.next();
      }
    },
    next() {
      const propertyOwner = {
        email: this.email,
        phone: this.phone,
        street: this.streetNumber,
        streetNumber: this.ownerStreetNumber,
        address1: this.address1,
        address2: this.address2,
        unitNumber: this.unitNumber,
        zip: this.zipCode,
        city: this.city,
        state: this.state ? this.state.value : "",
        county: this.county,
        lat: this.lat,
        lng: this.lng,
        isCommercial: this.property_owner_is_entity.length
          ? 1
          : 0
      };
      if (propertyOwner.isCommercial) {
        propertyOwner["entityName"] = this.entityName;
        propertyOwner["contactFirstName"] = this.contactFirstName;
        propertyOwner["contactLastName"] = this.contactLastName;
        if (this.naic) {
          propertyOwner["naicCode"] = this.naic;
        }
        else {
          propertyOwner["naicCode"] = { code: 238990 };
        }
      } else {
        propertyOwner["firstName"] = this.firstName,
        propertyOwner["lastName"] = this.lastName;
        propertyOwner["naicCode"] = { code: 238990 };
      }
      this.$emit("format-property", propertyOwner);
      if(this.computeTotalPropValue(0) > 100000) {
        this.$emit("set-tria", 0);
        this.$emit("set-premium", 0);
        this.$emit("next-step-payment");
      }
      else {
        this.$emit("next-step");
      }
    },
    prev() {
      this.$emit("prev-step");
    },
    getAddress($event) {
      const { addressData, placeResultData } = $event;
      this.lat = addressData.latitude.toString();
      this.lng = addressData.longitude.toString();
      let types = [];

      for (let i = 0; i < placeResultData.address_components.length; i++) {
        let type = placeResultData.address_components[i].types[0]; 
        let componentForm = {
          street_number: "short_name",
          route: "long_name",
          locality: "long_name",
          administrative_area_level_1: "short_name",
          administrative_area_level_2: "short_name",
          country: "long_name",
          postal_code: "short_name"
        };
        types.push(type);
        switch (type) {
        case "street_number":
          this.streetNumber = placeResultData.address_components[i][componentForm[type]];
          this.ownerStreetNumber = placeResultData.address_components[i][componentForm[type]];
          this.removeErrorMessage("streetNumber", this.streetNumber);
          break;

        case "route":
          this.streetNumber = this.streetNumber + " " + placeResultData.address_components[i][componentForm[type]];
          this.address1 = placeResultData.address_components[i][componentForm[type]];
          this.removeErrorMessage("streetNumber", this.streetNumber);
          break;

        case "address2":
          this.address2 = placeResultData.address_components[i][componentForm[type]];
          break;

        case "locality":
          this.city = placeResultData.address_components[i][componentForm[type]];
          this.removeErrorMessage("city", this.city);
          break;
        
        case "administrative_area_level_1":
          this.state = {
            value: placeResultData.address_components[i][componentForm[type]],
            display: STATES[placeResultData.address_components[i][componentForm[type]]]
          };
          this.removeErrorMessage("state", this.state);
          break;

        case "postal_code":
          this.zipCode = placeResultData.address_components[i][componentForm[type]];
          this.removeErrorMessage("zipCode", this.zipCode);
          break;
        case "administrative_area_level_2":
          this.county = placeResultData.address_components[i][componentForm[type]];
          break;
        }
      }
      if (types.indexOf("postal_code") < 0) {
        this.zipCode = "";
      }
    },
    handlePropertyOwnerEntityChange($event) {
      if (this.property_owner_is_entity.length) {
        this.property_owner_is_entity.pop($event.value);
      }  else {
        this.property_owner_is_entity.push($event.value);
      }    
    },
    handleStateChange($event) {
      if ($event) {
        this.errors["state"] = "";
      }

      this.state = $event;
    },
    removeErrorMessage(field, $event) {
      if (field === "state" && $event) {
        this.errors[field] = "";
      } else if ($event.trim().length) {
        this.errors[field] = "";
      }
    },
    computeTotalPropValue(price) {
      this.properties.forEach((property, index) => {
        if (index < this.property) {
          price += parseFloat(property.value)
        }
      });
      return price;
    },
  },
  computed: {
    ...mapState({
      naicCodes: state => state.equipment.naicCodes
    }),
    states() {
      let states = Object.keys(STATES).map(s => {
        return {
          value: s,
          display: STATES[s],
        };
      });
      return states;
    },
    entityOptions() {
      return [
        {
          value: "property_owner_is_entity",
          row_val: "property_owner_is_entity",
          display: "Property owner is company"
        }
      ];
    }
  },
  watch: {
    propertyOwner: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const { 
            entityName,
            naicCode,
            contactFirstName,
            contactLastName,
            firstName,
            lastName,
            email,
            phone,
            isCommercial, 
            street,
            unitNumber,
            zip,
            city,
            state
          } = newVal;

          if (entityName) {
            this.entityName = entityName;
          }
          if (naicCode) {
            this.naic = naicCode;
          }
          if (contactFirstName) {
            this.contactFirstName = contactFirstName;
          }
          if (contactLastName) {
            this.contactLastName = contactLastName;
          }
          if (firstName) {
            this.firstName = firstName;
          }
          if (lastName) {
            this.lastName = lastName;
          }
          if (email) {
            this.email = email;
          }
          if (phone) {
            this.phone = phone;
          }
          this.property_owner_is_entity = [];
          if (isCommercial == 1) {
            this.property_owner_is_entity.push(this.entityOptions[0].value);
          }
          if (street) {
            this.streetNumber = street;
          }
          if (unitNumber) {
            this.unitNumber = unitNumber;
          }
          if (zip) {
            this.zipCode = zip;
          }
          if (city) {
            this.city = city;
          }
          if (state) {
            this.state = {
              value: state,
              display: STATES[state]
            };
          }
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
.button-group {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  button:last-of-type {
    margin-left: $padding-lg;
  }
}
</style>
